const BASE_URL = 'http://127.0.0.1:8000/api';

// Debug function to log API requests
const logApiRequest = (url, method, body) => {
    console.log(`API Request: ${method} ${url}`);
    if (body) console.log('Request Body:', body);
};

// Debug function to log API responses
const logApiResponse = (url, status, data) => {
    console.log(`API Response: ${status} ${url}`);
    if (data) console.log('Response Data:', data);
};

export const authService = {
    // Register methods
    register: async (userData) => {
        try {
            const url = `${BASE_URL}/register-student/`;
            logApiRequest(url, 'POST', userData);

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(userData)
            });

            const data = await response.json();
            logApiResponse(url, response.status, data);

            if (!response.ok) {
                throw new Error(data.message || 'Registration failed');
            }

            return data;
        } catch (error) {
            console.error('Registration error:', error);
            throw error;
        }
    },

    registerTrainer: async (userData) => {
        try {
            const url = `${BASE_URL}/register-trainer/`;
            logApiRequest(url, 'POST', userData);

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(userData)
            });

            const data = await response.json();
            logApiResponse(url, response.status, data);

            if (!response.ok) {
                throw new Error(data.message || 'Trainer registration failed');
            }

            return data;
        } catch (error) {
            console.error('Trainer registration error:', error);
            throw error;
        }
    },

    registerCounselor: async (userData) => {
        try {
            const url = `${BASE_URL}/register-counselor/`;
            logApiRequest(url, 'POST', userData);

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(userData)
            });

            const data = await response.json();
            logApiResponse(url, response.status, data);

            if (!response.ok) {
                throw new Error(data.message || 'Counselor registration failed');
            }

            return data;
        } catch (error) {
            console.error('Counselor registration error:', error);
            throw error;
        }
    },

    // Login method with user type support
    login: async ({ email, password, userType }) => {
        try {
            const url = `${BASE_URL}/login/`;
            logApiRequest(url, 'POST', { email, password, userType });

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, password, userType })
            });

            const data = await response.json();
            logApiResponse(url, response.status, data);

            if (!response.ok) {
                throw new Error(data.message || 'Login failed');
            }

            return data;
        } catch (error) {
            console.error('Login error:', error);
            throw error;
        }
    },

    verifyOTP: async (data) => {
        try {
            const response = await fetch(`${BASE_URL}/verify-otp/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            });
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    // 4. Forgot Password
    forgotPassword: async (email) => {
        try {
            const response = await fetch(`${BASE_URL}/forgot-password/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to send reset link');
            }

            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    // 5. Reset Password
    resetPassword: async (data) => {
        try {
            const response = await fetch(`${BASE_URL}/reset-password/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: data.email,
                    otp: data.otp,
                    new_password: data.newPassword
                }),
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Password reset failed');
            }

            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    // 6. Change Password
    changePassword: async (data) => {
        const token = localStorage.getItem('token');
        if (!token) {
            throw new Error('No authentication token found');
        }

        try {
            const response = await fetch(`${BASE_URL}/change-password/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    current_password: data.currentPassword,
                    new_password: data.newPassword,
                    confirm_new_password: data.confirmNewPassword
                }),
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Password change failed');
            }

            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    // Helper function to handle token storage
    setToken: (token) => {
        if (token) {
            localStorage.setItem('token', token);
        } else {
            localStorage.removeItem('token');
        }
    },

    // Helper function to get token
    getToken: () => {
        return localStorage.getItem('token');
    },

    // Get Student Dashboard Data
    getStudentDashboard: async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await fetch(`${BASE_URL}/students/dashboard/`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                if (response.status === 404) {
                    throw new Error('Dashboard data not found');
                }
                throw new Error('Failed to fetch dashboard data');
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            throw error;
        }
    },

    // Get Student Profile
    getStudentProfile: async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await fetch(`${BASE_URL}/students/profile/`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                if (response.status === 404) {
                    throw new Error('Student profile not found');
                }
                throw new Error('Failed to fetch student profile');
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching student profile:', error);
            throw error;
        }
    },

    // Update Student Profile
    updateStudentProfile: async (profileData) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await fetch(`${BASE_URL}/students/profile/`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(profileData)
            });

            if (!response.ok) {
                throw new Error('Failed to update student profile');
            }

            return await response.json();
        } catch (error) {
            console.error('Error updating student profile:', error);
            throw error;
        }
    },

    // Trainer Login
    trainerLogin: async (credentials) => {
        try {
            const response = await fetch(`${BASE_URL}/trainer-login/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: credentials.email,
                    password: credentials.password,
                    role: 'trainer'
                }),
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Trainer login failed');
            }

            const data = await response.json();
            if (data.user.role !== 'trainer') {
                throw new Error('Invalid account type. Please use trainer credentials.');
            }

            return data;
        } catch (error) {
            throw error;
        }
    },

    // Counselor Login
    counselorLogin: async (credentials) => {
        try {
            const response = await fetch(`${BASE_URL}/counselor-login/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: credentials.email,
                    password: credentials.password,
                    role: 'counselor'
                }),
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Counselor login failed');
            }

            const data = await response.json();
            if (data.user.role !== 'counselor') {
                throw new Error('Invalid account type. Please use counselor credentials.');
            }

            return data;
        } catch (error) {
            throw error;
        }
    },

    updateUserProfile: async (profileData) => {
        const token = localStorage.getItem('token');
        if (!token) {
            throw new Error('No authentication token found');
        }

        try {
            const response = await fetch(`${BASE_URL}/user/profile/update/`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(profileData)
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to update profile');
            }

            return await response.json();
        } catch (error) {
            throw error;
        }
    }
};

// Student Dashboard API
export const dashboardService = {
    getStudentDashboard: async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            throw new Error('No authentication token found');
        }

        try {
            const response = await fetch(`${BASE_URL}/dashboard/student/`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to fetch dashboard data');
            }

            return await response.json();
        } catch (error) {
            throw error;
        }
    }
};

// User Profile API
export const profileService = {
    getUserProfile: async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            throw new Error('No authentication token found');
        }

        try {
            const response = await fetch(`${BASE_URL}/user/profile/`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to fetch profile data');
            }

            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    updateUserProfile: async (profileData) => {
        const token = localStorage.getItem('token');
        if (!token) {
            throw new Error('No authentication token found');
        }

        try {
            const response = await fetch(`${BASE_URL}/user/profile/update/`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(profileData)
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to update profile');
            }

            return await response.json();
        } catch (error) {
            throw error;
        }
    }
};

// Trainer Profile API
export const trainerService = {
    // Get Trainer Profile
    getTrainerProfile: async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            throw new Error('No authentication token found');
        }

        try {
            const response = await fetch(`${BASE_URL}/trainers/profile/`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to fetch trainer profile');
            }

            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    // Update Trainer Profile
    updateTrainerProfile: async (profileData) => {
        const token = localStorage.getItem('token');
        if (!token) {
            throw new Error('No authentication token found');
        }

        try {
            const response = await fetch(`${BASE_URL}/trainers/profile/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(profileData)
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to update trainer profile');
            }

            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    // Get Trainer Dashboard Data
    getTrainerDashboard: async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            throw new Error('No authentication token found');
        }

        try {
            const response = await fetch(`${BASE_URL}/trainers/dashboard/`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to fetch trainer dashboard data');
            }

            return await response.json();
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch trainer dashboard data');
        }
    }
};

// Counsellor Profile API
export const counsellorService = {
    // Get Counsellor Profile
    getCounsellorProfile: async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            throw new Error('No authentication token found');
        }

        try {
            const response = await fetch(`${BASE_URL}/counsellors/profile/`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to fetch counsellor profile');
            }

            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    // Update Counsellor Profile
    updateCounsellorProfile: async (profileData) => {
        const token = localStorage.getItem('token');
        if (!token) {
            throw new Error('No authentication token found');
        }

        try {
            const response = await fetch(`${BASE_URL}/counsellors/profile/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(profileData)
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to update counsellor profile');
            }

            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    // Get Counsellor Dashboard Data
    getCounsellorDashboard: async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            throw new Error('No authentication token found');
        }

        try {
            const response = await fetch(`${BASE_URL}/counsellors/dashboard/`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to fetch counsellor dashboard data');
            }

            return await response.json();
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch counsellor dashboard data');
        }
    }
};

export default authService;